import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DialogService } from 'src/app/services/dialog.service';
import { LayoutService } from 'src/app/services/layout.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';
import { SignalRService } from 'src/app/services/signalr.service';
import { PlanogramService } from 'src/app/services/planogram.service';
import { ToastService } from 'src/app/services/toasts.service';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-refresh-planogram-modal',
    templateUrl: './refresh-planogram-modal.component.html',
    styleUrl: './refresh-planogram-modal.component.scss',
    imports: [FormsModule]
})
export class RefreshPlanogramModalComponent implements OnInit {
  stateName: string = '';
  salesPeriodStartMonth: string;
  salesPeriodEndMonth: string;
  addBarcodes: string[] = [];
  removeBarcodes: string[] = [];
  newAddBarcode: string = '';
  newRemoveBarcode: string = '';
  isRefreshing: boolean = false;

  // Summary stats
  currentProductCount: number = 0;
  layoutBreakdown: Map<number, number> = new Map<number, number>();

  private destroyRef = inject(DestroyRef);

  constructor(
    public activeModal: NgbActiveModal,
    private planogramService: PlanogramService,
    private layoutService: LayoutService,
    private planogramApiService: PlanogramApiService,
    private signalRService: SignalRService,
    private toastService: ToastService,
    private dialogService: DialogService
  ) {
    // Default sales period to last 3 months
    const today = new Date();
    this.salesPeriodEndMonth = this.toMonthString(today);
    this.salesPeriodStartMonth = this.toMonthString(new Date(today.getFullYear(), today.getMonth() - 3, 1));
  }

  private toMonthString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  private startOfMonth(monthString: string): Date {
    const [year, month] = monthString.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }

  private endOfMonth(monthString: string): Date {
    const [year, month] = monthString.split('-').map(Number);
    return new Date(year, month, 0, 23, 59, 59, 999);
  }

  ngOnInit(): void {
    this.stateName = `${this.planogramService.getStoreName()} - ${this.planogramService.getMerchName()}`;
    this.currentProductCount = this.layoutService.planogramItems.size;
    
    this.layoutService.layoutBreakdown$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(breakdown => {
      this.layoutBreakdown = breakdown;
    });
  }

  // Add barcode to add list
  addToAddList(): void {
    const barcode = this.newAddBarcode.trim();
    if (barcode && !this.addBarcodes.includes(barcode) && !this.removeBarcodes.includes(barcode)) {
      if (this.isProductInPlanogram(barcode)) {
        this.toastService.show('Product is already in planogram', {
          classname: 'bg-warning text-dark p-3 fs-5',
          delay: 3000
        });
        return;
      }
      this.addBarcodes.push(barcode);
      this.newAddBarcode = '';
    }
  }

  // Add barcode to remove list
  addToRemoveList(): void {
    const barcode = this.newRemoveBarcode.trim();
    if (barcode && !this.removeBarcodes.includes(barcode) && !this.addBarcodes.includes(barcode)) {
      if (!this.isProductInPlanogram(barcode)) {
        this.toastService.show('Product is not in planogram', {
          classname: 'bg-warning text-dark p-3 fs-5',
          delay: 3000
        });
        return;
      }
      this.removeBarcodes.push(barcode);
      this.newRemoveBarcode = '';
    }
  }

  // Remove barcode from add list
  removeFromAddList(barcode: string): void {
    this.addBarcodes = this.addBarcodes.filter(b => b !== barcode);
  }

  // Remove barcode from remove list
  removeFromRemoveList(barcode: string): void {
    this.removeBarcodes = this.removeBarcodes.filter(b => b !== barcode);
  }

  // Check if product exists in planogram items
  isProductInPlanogram(barcode: string): boolean {
    for (const item of this.layoutService.planogramItems.values()) {
      if (item.barcode === barcode) {
        return true;
      }
    }
    return false;
  }

  // Check if a barcode is on the layout (physically placed)
  isOnLayout(barcode: string): boolean {
    const productId = this.getProductIdByBarcode(barcode);
    if (productId === null) return false;
    return (this.layoutBreakdown.get(productId) ?? 0) > 0;
  }

  // Get product ID by barcode
  getProductIdByBarcode(barcode: string): number | null {
    for (const item of this.layoutService.planogramItems.values()) {
      if (item.barcode === barcode) {
        return item.productId;
      }
    }
    return null;
  }

  // Format date for display
  formatDate(date: Date): string {
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  }

  // Validate form
  isValid(): boolean {
    return !!this.salesPeriodStartMonth
      && !!this.salesPeriodEndMonth
      && this.salesPeriodStartMonth <= this.salesPeriodEndMonth;
  }

  async confirm(): Promise<void> {
    if (!this.isValid()) {
      this.toastService.show('Invalid sales period - start month must be on or before end month', {
        classname: 'bg-danger text-light p-3 fs-5',
        delay: 3000
      });
      return;
    }

    const salesPeriodStart = this.startOfMonth(this.salesPeriodStartMonth);
    const salesPeriodEnd = this.endOfMonth(this.salesPeriodEndMonth);

    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();

    if (!planogramId || !stateId) {
      await this.dialogService.showError('Planogram or state ID is missing');
      return;
    }

    const confirmed = await this.dialogService.showConfirmation(
      'Refresh Planogram',
      'This will create a new state with updated product data. Continue?'
    );

    if (!confirmed) return;

    this.isRefreshing = true;

    try {
      const response = await this.planogramApiService.refreshPlanogram(
        {
          planogramId: planogramId,
          stateId: stateId,
          name: this.stateName || null,
          salesPeriodStart: salesPeriodStart,
          salesPeriodEnd: salesPeriodEnd,
          addBarcodes: this.addBarcodes,
          removeBarcodes: this.removeBarcodes
        }
      );

      if (response) {
        this.toastService.show('Planogram refreshed successfully', {
          classname: 'bg-success text-light p-3 fs-5',
          delay: 3000
        });
        this.signalRService.loadPlanogramState(response.stateId);
        this.activeModal.close(response);
      } else {
        this.toastService.show('Failed to refresh planogram', {
          classname: 'bg-danger text-light p-3 fs-5',
          delay: 3000
        });
      }
    } catch (error: any) {
      await this.dialogService.showError(error.message || 'An error occurred while refreshing the planogram');
      console.error('Refresh planogram error:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}
